import { io, Socket } from 'socket.io-client';

const SOCKET_URL = process.env.REACT_APP_API_BASE_URL;
if (!SOCKET_URL) throw new Error('REACT_APP_API_BASE_URL is not defined');

let socket: Socket | null = null;
let isReconnecting = false;
let latencyMs: number | null = null;
let pingIntervalId: ReturnType<typeof setInterval> | null = null;

/** Tentatives illimitées jusqu'à retour réseau (Socket.IO client). */
const RECONNECTION_ATTEMPTS = Infinity;
const PING_INTERVAL_MS = 5000;

const buildOptions = (token: string) => ({
  autoConnect: true,
  reconnection: true,
  reconnectionAttempts: RECONNECTION_ATTEMPTS,
  reconnectionDelay: 500,
  reconnectionDelayMax: 2000,
  // Préféré côté serveur (handshake.auth.token), compatible WebSocket pur.
  auth: { token: `Bearer ${token}` },
  // websocket en premier (chemin rapide, inchangé) ; polling en repli si l'upgrade
  // WS échoue ou est instable (réseau mobile) — évite qu'une micro-coupure réseau
  // dégénère en vraie déconnexion faute de filet de sécurité pour la reconnexion.
  transports: ['websocket', 'polling'],
});

const attachLifecycleListeners = (s: Socket): void => {
  s.on('reconnect_attempt', () => {
    isReconnecting = true;
  });

  s.on('reconnect', () => {
    isReconnecting = false;
  });

  s.on('reconnect_failed', () => {
    isReconnecting = false;
  });

  s.on('connect', () => {
    isReconnecting = false;
  });

  s.on('disconnect', () => {
    isReconnecting = false;
    latencyMs = null;
  });

  s.on('pong', (sentAt: number) => {
    latencyMs = Date.now() - sentAt;
  });
};

const sendPing = (s: Socket): void => {
  if (!s.connected) return;
  s.emit('ping', Date.now());
};

const startLatencyProbe = (s: Socket): void => {
  stopLatencyProbe();
  sendPing(s);
  pingIntervalId = setInterval(() => sendPing(s), PING_INTERVAL_MS);
};

const stopLatencyProbe = (): void => {
  if (pingIntervalId != null) {
    clearInterval(pingIntervalId);
    pingIntervalId = null;
  }
  latencyMs = null;
};

export const createInstanceSocket = (token: string): Socket => {
  // Évite les doubles instances : on ferme proprement avant de recréer.
  if (socket) {
    socket.removeAllListeners();
    socket.disconnect();
    socket = null;
  }

  socket = io(SOCKET_URL, buildOptions(token));
  attachLifecycleListeners(socket);
  startLatencyProbe(socket);
  return socket;
};

export const disconnectSocket = (): void => {
  stopLatencyProbe();
  if (!socket) return;
  socket.removeAllListeners();
  socket.disconnect();
  socket = null;
};

export const getSocket = (): Socket | null => socket;

export const getReconnectionStatus = (): boolean => isReconnecting;

export const getConnectionStatus = (): boolean =>
  socket ? socket.connected : false;

export const getLatency = (): number | null => latencyMs;

// === Helpers debug / tests ===
export const forceDisconnect = (): void => {
  if (!socket) return;
  socket.disconnect();
  setTimeout(() => {
    if (socket && !socket.connected) socket.connect();
  }, 5000);
};

export const forceReconnect = (): void => {
  if (!socket) return;
  socket.connect();
};

export const simulateNetworkDisconnect = (): void => {
  if (!socket) return;
  socket.io.engine.close();
};

export const createTestSocket = (): void => {
  if (socket) return;
  socket = io(SOCKET_URL, {
    autoConnect: true,
    reconnection: true,
    reconnectionAttempts: RECONNECTION_ATTEMPTS,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
  });
  attachLifecycleListeners(socket);
};

export { socket };
